import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

const VARIANTS = [
  { p: 3, q: 6, r: 2, s: 7, value: 18 },
  { p: 2, q: 7, r: 3, s: 6, value: 24 },
  { p: 4, q: 9, r: 5, s: 8, value: 32 },
  { p: 1, q: 8, r: 2, s: 7, value: 12 },
  { p: 5, q: 10, r: 6, s: 9, value: 50 },
  { p: 3, q: 8, r: 4, s: 7, value: 27 },
  { p: 2, q: 9, r: 4, s: 7, value: 16 },
  { p: 4, q: 7, r: 5, s: 6, value: 45 },
];

const pick = (items) => items[Math.floor(Math.random() * items.length)];
const term = (index) => `a_{${index}}`;

export function generateTask(options = {}) {
  const original = options.variant === "original" && options.session === "2026_MAJ";
  const data = original ? VARIANTS[0] : pick(VARIANTS);
  return {
    type: "MaturaGeometricEqualIndexSumProductOpenTask",
    data: { ...data, maxPoints: 1 },
    source: {
      variant: original ? "original" : "random",
      exam: "MATURA",
      session: original ? "2026_MAJ" : undefined,
      taskNumber: "17",
      basedOn: "MMAP-P0-100-A-2605-17",
    },
  };
}

const numberValue = (value) => Number(String(value?.raw ?? value ?? "").replace(",", "."));

export function renderTask({ data }) {
  return (
    <div>
      <p>Ciąg <InlineMath math="(a_n)" /> jest geometryczny i wszystkie jego wyrazy są dodatnie. Wiadomo, że</p>
      <BlockMath math={`${term(data.p)}\\cdot ${term(data.q)}=${data.value}.`} />
      <p>Oblicz <InlineMath math={`${term(data.r)}\\cdot ${term(data.s)}`} />.</p>
    </div>
  );
}

export function renderInput({ data }, value = {}, onChange, disabled) {
  return <FractionInput value={value?.raw ?? value ?? ""} onChange={onChange} disabled={disabled} label={`${term(data.r)}\\cdot ${term(data.s)}`} />;
}

export function checkAnswer({ data }, value = {}) {
  return Math.abs(numberValue(value) - data.value) < 1e-9;
}

export function renderAnswer({ data }) {
  return <InlineMath math={String(data.value)} />;
}

export function renderUserAnswer(value = {}, task) {
  const label = task?.data ? `${term(task.data.r)}\\cdot ${term(task.data.s)}` : "x";
  return <InlineMath math={`${label}=${String(value?.raw ?? value ?? "-")}`} />;
}

export function buildExplanationModel(data) {
  const givenExponent = data.p + data.q - 2;
  const soughtExponent = data.r + data.s - 2;
  return {
    generalTerm: "a_n=a_1\\cdot q^{n-1}",
    givenProduct: `${term(data.p)}\\cdot ${term(data.q)}=(a_1\\cdot q^{${data.p - 1}})\\cdot(a_1\\cdot q^{${data.q - 1}})=a_1^2\\cdot q^{${givenExponent}}`,
    soughtProduct: `${term(data.r)}\\cdot ${term(data.s)}=(a_1\\cdot q^{${data.r - 1}})\\cdot(a_1\\cdot q^{${data.s - 1}})=a_1^2\\cdot q^{${soughtExponent}}`,
    equalSums: `${data.p}+${data.q}=${data.r}+${data.s}`,
    equalProducts: `${term(data.r)}\\cdot ${term(data.s)}=${term(data.p)}\\cdot ${term(data.q)}=${data.value}`,
  };
}

export function renderExplanation({ data }) {
  const model = buildExplanationModel(data);
  return (
    <div style={{ display: "grid", gap: 12 }}>
      <p><strong>Krok 1.</strong> Korzystamy ze wzoru na wyraz ciągu geometrycznego.</p>
      <BlockMath math={model.generalTerm} />

      <p><strong>Krok 2.</strong> Rozpisujemy iloczyn podany w treści zadania.</p>
      <BlockMath math={model.givenProduct} />

      <p><strong>Krok 3.</strong> W ten sam sposób rozpisujemy szukany iloczyn.</p>
      <BlockMath math={model.soughtProduct} />

      <p><strong>Krok 4.</strong> Sumy indeksów są równe, więc w obu iloczynach otrzymujemy tę samą potęgę ilorazu <InlineMath math="q" />.</p>
      <BlockMath math={model.equalSums} />

      <p><strong>Wniosek.</strong> Oba iloczyny są równe.</p>
      <BlockMath math={model.equalProducts} />
    </div>
  );
}
